/**
 * Cross-tab DM "currently viewing" sync.
 * Background tabs suppress OS notify / toast / sound when another tab
 * already has the same conversation open.
 */

const CHANNEL_NAME = "circa-dm-viewing";
const STORAGE_KEY = "circa.dm.viewing";
const TTL_MS = 5000;
const HEARTBEAT_MS = 2000;

const tabId =
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `tab-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

/** @type {Map<string, { orgId: string, conversationId: number, tabId: string, ts: number }>} */
const remoteByTab = new Map();
const listeners = new Set();

let channel = null;
let heartbeatTimer = null;
let localViewing = null; // { orgId, conversationId } | null
let storageListenerAttached = false;

function convKey(orgId, conversationId) {
  return `${orgId}:${Number(conversationId)}`;
}

function pruneRemote(now = Date.now()) {
  for (const [id, entry] of remoteByTab) {
    if (!entry?.ts || now - entry.ts > TTL_MS) {
      remoteByTab.delete(id);
    }
  }
}

function notifyListeners() {
  for (const fn of listeners) {
    try {
      fn();
    } catch {
      /* ignore */
    }
  }
}

function ensureChannel() {
  if (channel || typeof BroadcastChannel === "undefined") return channel;
  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (ev) => {
      applyRemote(ev?.data);
    };
  } catch {
    channel = null;
  }
  return channel;
}

function ensureStorageListener() {
  if (storageListenerAttached || typeof window === "undefined") return;
  storageListenerAttached = true;
  window.addEventListener("storage", (ev) => {
    if (ev.key !== STORAGE_KEY || !ev.newValue) return;
    try {
      applyRemote(JSON.parse(ev.newValue));
    } catch {
      /* ignore */
    }
  });
}

function applyRemote(msg) {
  if (!msg || typeof msg !== "object") return;
  if (msg.tabId === tabId) return;
  const now = Date.now();
  if (msg.type === "clear") {
    remoteByTab.delete(msg.tabId);
    pruneRemote(now);
    notifyListeners();
    return;
  }
  if (msg.type === "view" && msg.orgId && msg.conversationId != null) {
    remoteByTab.set(msg.tabId, {
      orgId: String(msg.orgId),
      conversationId: Number(msg.conversationId),
      tabId: String(msg.tabId),
      ts: Number(msg.ts) || now,
    });
    pruneRemote(now);
    notifyListeners();
  }
}

function broadcast(payload) {
  ensureChannel();
  ensureStorageListener();
  const body = { ...payload, tabId, ts: Date.now() };
  try {
    channel?.postMessage(body);
  } catch {
    /* ignore */
  }
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(body));
    }
  } catch {
    /* ignore */
  }
}

function stopHeartbeat() {
  if (heartbeatTimer != null) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

function startHeartbeat() {
  stopHeartbeat();
  heartbeatTimer = setInterval(() => {
    if (!localViewing) return;
    if (typeof document !== "undefined" && document.hidden) return;
    broadcast({
      type: "view",
      orgId: localViewing.orgId,
      conversationId: localViewing.conversationId,
    });
  }, HEARTBEAT_MS);
}

/**
 * Mark this tab as viewing a conversation (or clear with nulls).
 * No-ops when the document is hidden — clears remote claim instead.
 */
export function setLocalDmViewing(orgId, conversationId) {
  const cid = Number(conversationId);
  const oid = orgId != null ? String(orgId) : "";
  const valid = Boolean(oid && Number.isFinite(cid) && cid > 0);
  const visible = typeof document === "undefined" || !document.hidden;

  if (!valid || !visible) {
    if (localViewing) {
      localViewing = null;
      stopHeartbeat();
      broadcast({ type: "clear" });
    }
    return;
  }

  localViewing = { orgId: oid, conversationId: cid };
  broadcast({
    type: "view",
    orgId: oid,
    conversationId: cid,
  });
  startHeartbeat();
}

/** True if this tab currently claims the conversation (visible DmThread). */
export function isDmViewingHere(orgId, conversationId) {
  if (!orgId || conversationId == null || !localViewing) return false;
  if (typeof document !== "undefined" && document.hidden) return false;
  return (
    String(localViewing.orgId) === String(orgId) &&
    Number(localViewing.conversationId) === Number(conversationId)
  );
}

/** True if another tab recently claimed this conversation. */
export function isDmViewedElsewhere(orgId, conversationId) {
  if (!orgId || conversationId == null) return false;
  pruneRemote();
  const key = convKey(orgId, conversationId);
  for (const entry of remoteByTab.values()) {
    if (convKey(entry.orgId, entry.conversationId) === key) return true;
  }
  return false;
}

/** Subscribe to remote viewing changes; returns unsubscribe. */
export function subscribeDmViewing(listener) {
  ensureChannel();
  ensureStorageListener();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getDmViewingTabId() {
  return tabId;
}
